import React, { Dispatch, SetStateAction } from 'react';
import useModal from '@/hooks/useModal';
import { AccountDetailCard, AvatarName } from '@/components/dashboard';
import Icons from '@/const/icons';
import {
	Button,
	EmptyGeneral,
	FunnelEmptyScreen,
	Icon,
	LabelLawyerState,
	LabelShowingResults,
	Typography,
} from '@/components/common';
import { formatPhoneNumber } from '@/utils';
import clsx from 'clsx';
import SimpleTable from '@/components/common/tables/simpleTable';
import { useRouter } from 'next/router';
import { Input } from '@/components/form';
import { useForm } from 'react-hook-form';
import { PhaseFilter } from './filters/PhaseFilter';
import { StatusFilter } from './filters/StatusFilter';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
	GET_LAWYER_DETAIL,
	GET_LAWYER_DETAIL_CASES_LIST,
	userLawyerService,
} from '@/api';
import {
	LawyerDetailCase,
	LawyerDetailCaseList,
	TeamMember,
} from '@/interfaces';
import AppRoutes from '@/const/routes';
import { useFilters } from '@/hooks';

interface Information {
	key: string;
	icon: string;
	value: string | undefined;
	isVerified: boolean | undefined;
}

interface Props {
	id: string | undefined;
	setId: Dispatch<SetStateAction<string | undefined>>;
}

export const ModalLawyerDetail: React.FC<Props> = ({ id, setId }) => {
	const router = useRouter();
	const { phase, status } = router.query;
	const observer = React.useRef<IntersectionObserver | null>(null);
	const [information, setInformation] = React.useState<Information[]>([]);
	const [lawyerInfo, setLawyerInfo] = React.useState<Partial<TeamMember>>();
	const search = router.query['search-case'];

	const { Modal, show, hide } = useModal();
	const { register } = useForm();
	const { searchFilter, hasFilter } = useFilters();

	const fetchData = async ({
		pageParam = 1,
	}: {
		pageParam?: number;
	}): Promise<LawyerDetailCaseList> => {
		const response = await userLawyerService.getLawyerCases(
			(id as string) || '',
			pageParam,
			phase as string,
			status as string,
			search as string
		);
		return response.data;
	};

	const { data: lawyer } = useQuery({
		queryKey: [GET_LAWYER_DETAIL, id],
		queryFn: () => userLawyerService.getLawyerDetail(id as string),
		enabled: !!id,
	});

	const {
		data: cases,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isLoading: isLoadingCases,
	} = useInfiniteQuery({
		queryKey: [GET_LAWYER_DETAIL_CASES_LIST, id, phase, status, search],
		queryFn: fetchData,
		getNextPageParam: (lastPage) => {
			return lastPage.page < lastPage.totalPages
				? lastPage.page + 1
				: undefined;
		},
		initialPageParam: 1,
	});

	React.useEffect(() => {
		id ? show() : hide();
	}, [id]);

	React.useEffect(() => {
		if (!lawyer?.data) return;
		const data = [
			{
				key: 'Primary email',
				icon: Icons.sms,
				value: lawyer.data.email,
				isVerified: lawyer.data.is_email_verified,
			},
			{
				key: 'Address',
				icon: Icons.location,
				value: lawyer.data.address,
				isVerified: false,
			},
			{
				key: 'Phone number',
				icon: Icons.call,
				value: lawyer.data.phonenumber,
				isVerified: lawyer.data.is_phone_number_verified,
			},
		];

		setInformation(data);
		setLawyerInfo({
			firstname: lawyer.data.firsname,
			lastname: lawyer.data.lastname,
			role: lawyer.data.role,
		});
	}, [lawyer]);

	const lastItemCallback = React.useCallback(
		(node: HTMLDivElement | HTMLLIElement | null) => {
			if (isFetchingNextPage) return;

			if (observer.current) observer.current.disconnect();
			observer.current = new IntersectionObserver((entries) => {
				if (entries[0].isIntersecting && hasNextPage) {
					fetchNextPage();
				}
			});

			if (node) observer.current.observe(node);
		},
		[isFetchingNextPage, hasNextPage, fetchNextPage]
	);

	const data = cases?.pages.flatMap((page) => page.items) ?? [];
	const total = cases?.pages.reduce((acc, curr) => {
		acc += curr.total;
		return acc;
	}, 0);
	const totalItems = cases?.pages[0]?.totalItems ?? 0;
	const appliedFilters = hasFilter('phase') || hasFilter('status');

	return (
		<Modal
			withXButton
			classNameCardContainer="!w-[1288px] h-[95%] !py-16 !px-12"
			classNameContainerChildren="!overflow-y-hidden flex flex-col"
			backgroundColor="white"
			onClose={() => setId(undefined)}
		>
			<div className="">
				<AvatarName
					isBold
					photoUrl={lawyer?.data.profile_picture ?? ''}
					size="small"
					firstname={lawyerInfo?.firstname}
					lastname={lawyerInfo?.lastname}
					classNameContainer="!items-start mb-8"
					smallText={lawyerInfo?.role}
				/>
				<AccountDetailCard icon={Icons.call} title="Contact Information">
					<ul className="grid grid-cols-2 gap-4">
						{information.map((item, index) => {
							if (!item.value) return;
							return (
								<li
									key={index}
									className={clsx(
										'flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0',
										{
											'': index,
										}
									)}
								>
									<div className="w-full sm:w-28 text-left">
										<Typography type="regular-12" className="opacity-60">
											{item.key}
										</Typography>
									</div>
									<div className="flex items-center gap-2">
										<Icon src={item.icon} className="!w-4 !h-4 text-green-60" />
										<Typography type="regular-14">
											{index === 2 && formatPhoneNumber(item.value)
												? formatPhoneNumber(item.value)
												: item.value}
										</Typography>
										{item.isVerified && (
											<div className="flex items-center gap-2">
												<Icon
													src={Icons.checkCircleGreen}
													className="!w-4 !h-4"
												/>
												<Typography
													type="regular-12"
													className="text-green-130"
												>
													Verified
												</Typography>
											</div>
										)}
									</div>
								</li>
							);
						})}
					</ul>
				</AccountDetailCard>
			</div>
			<div className="flex flex-col h-[90%] min-h-0">
				<div className="flex items-center gap-2 mt-6">
					<Icon src={Icons.briefcase} className="!size-5 text-primary" />
					<Typography type="bold-16">Lawyer Cases</Typography>
				</div>
				<div className="flex justify-between items-center gap-x-2 mt-4">
					<Input
						name="search"
						register={register}
						placeholder="Search"
						iconLeft={Icons.search}
						classNameIcon={'!top-[23%] !w-4 !h-4 text-opacity-black'}
						classNameInput="pl-9 xl:w-[417px] !bg-gray-15 !h-[33px] !m-0 border-none 3xl:!text-sm"
						onChange={({ target }) => searchFilter(target.value, 'search-case')}
					/>
					<LabelShowingResults
						total={total ?? 0}
						totalItems={totalItems}
						labelTotalItems={'cases'}
						isSearched={false}
						allowDynamicUpdate={true}
					/>
				</div>
				<div className="mt-4 flex-1 min-h-0 overflow-y-auto scroll-custom xl:mb-10 1xl:mb-7 2xl:mb-0 [@media(max-height:780px)]:mb-7">
					<SimpleTable<LawyerDetailCase>
						isLoading={isLoadingCases}
						hasPagination={false}
						lastItemRef={lastItemCallback}
						classNameContainer={clsx('border-none shadow-none h-full')}
						classNameHead="bg-white !opacity-100"
						classNameHeader="grid grid-cols-4 grid-cols-[repeat(3,_minmax(0,_1fr))_0.5fr]"
						classNameRows="grid grid-cols-4 grid-cols-[repeat(3,_minmax(0,_1fr))_0.5fr] items-center cursor-pointer text-black"
						totalPages={0}
						hideRowActions
						columns={[
							{
								header: 'Case',
								content: (instance) => (
									<Typography type="regular-14">{instance.title}</Typography>
								),
							},
							{
								header: <PhaseFilter />,
								content: (instance) => (
									<Typography type="regular-14">
										{instance.case_phase}
									</Typography>
								),
							},
							{
								header: <StatusFilter />,
								content: (instance) => (
									<LabelLawyerState status={instance.case_progress} />
								),
							},
							{
								header: '',
								content: (instance) => (
									<Button
										href={`${AppRoutes.LAWYERAPP_CASES}/${instance.id}/investigation`}
										decoration="line-primary"
										size="small"
									>
										View Case
									</Button>
								),
							},
						]}
						rows={data}
						tableEmptyComponent={
							data?.length === 0 && !appliedFilters ? (
								<div className="flex justify-center pt-[88px]">
									<EmptyGeneral />
								</div>
							) : (
								<div className="flex justify-center pt-[88px]">
									<FunnelEmptyScreen
										message="There are currently no cases matching this filter"
										// className="mb-[15rem] mt-5"
									/>
								</div>
							)
						}
					/>
				</div>
			</div>
		</Modal>
	);
};
